import { DialogSize, InputErrorHintComponent, SharedModule, SnackbarService, SnackbarType } from '@aiwyn/shared';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
// Imported by path, not via the `@aiwyn/api` barrel: that barrel pulls in the whole API layer, and
// reaching it from `common/layout` risks the same cycle the sibling firm-contact work hit with
// `@aiwyn/services`. `import/no-cycle` is an ESLint error here with a CI baseline.
import { SupportRequestApiService } from '../../../../api/services/support-request-api.service';

/**
 * Values the header resolves at click time and hands in.
 *
 * Read in the header rather than inside the dialog on purpose: the auth state can briefly revert
 * after a dialog opens, and a field that populates then empties looks broken. Both are display-only
 * — the server derives the authoritative name and reply address from the verified session.
 */
export interface ContactSupportDialogData {
  name: string;
  email: string;
}

const TOO_MANY_REQUESTS = 429;

/**
 * In-app "Contact Support" form. Replaces the help menu's `mailto:`, which does nothing at all for a
 * client without a desktop mail client and gives no sign that nothing was sent.
 *
 * Name and email are rendered read-only. The ticket calls them auto-populated fields, and they are —
 * but they are not editable, because an editable email would let the sender choose where the firm's
 * reply lands. The server ignores any address it did not derive itself, so an editable input would
 * promise something the backend refuses to honour.
 *
 * The destination is asked of the server rather than derived here. A firm that has configured no
 * contact address is routed to Aiwyn's support desk, and showing the client which of the two they
 * are writing to is what keeps that honest — so the address displayed has to be the one the send
 * resolves, not a second guess at it.
 */
@Component({
  selector: 'app-contact-support-dialog',
  templateUrl: './contact-support-dialog.component.html',
  styleUrls: ['./contact-support-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, SharedModule, InputErrorHintComponent],
})
export class ContactSupportDialogComponent {
  private static readonly MAX_DESCRIPTION_LENGTH = 5000;

  readonly DialogSize = DialogSize;

  private readonly dialogRef = inject(MatDialogRef<ContactSupportDialogComponent>);
  private readonly supportRequestApi = inject(SupportRequestApiService);
  private readonly snackbar = inject(SnackbarService);

  readonly data: ContactSupportDialogData = inject(MAT_DIALOG_DATA);

  readonly isSubmitting = signal(false);

  /**
   * Empty until the server answers, and left empty if it never does. A failed lookup must not block
   * the send — the message still reaches whatever the server resolves, and the confirmation names
   * that address — so the line is hidden rather than replaced with a guess.
   */
  readonly destination = signal('');

  readonly form = new FormGroup({
    description: new FormControl('', [
      ContactSupportDialogComponent.notBlank,
      Validators.maxLength(ContactSupportDialogComponent.MAX_DESCRIPTION_LENGTH),
    ]),
  });

  /**
   * `Validators.required` only checks emptiness, so a description of spaces or newlines passes it — the
   * user would get a spinner and then a 400 from the server's `@NotBlank`. Reports the `required` key so
   * the shared error hint renders the existing "Field is required" message with no extra wiring.
   */
  private static notBlank(control: AbstractControl): ValidationErrors | null {
    return (control.value ?? '').trim().length === 0 ? { required: true } : null;
  }

  readonly descriptionControl = this.form.controls.description;

  /** Mirrors the server's own cap so the user is told before a round trip rejects it. */
  readonly maxLength = ContactSupportDialogComponent.MAX_DESCRIPTION_LENGTH;

  /**
   * A getter, not a `computed()`: a reactive `FormControl` value is not a signal, so a computed would
   * capture the initial length and never recompute. Typing in the textarea marks this component dirty,
   * so OnPush re-evaluates the getter on the next check.
   */
  get remaining(): number {
    return this.maxLength - (this.descriptionControl.value?.length ?? 0);
  }

  constructor() {
    this.supportRequestApi
      .destination()
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: ({ destination }) => this.destination.set(destination),
        error: () => this.destination.set(''),
      });
  }

  submit(): void {
    // The shared error hint only renders once a control is touched or dirty, so an untouched form
    // would otherwise appear to silently do nothing on the first click.
    this.form.markAllAsTouched();

    if (this.form.invalid || this.isSubmitting()) {
      return;
    }

    this.isSubmitting.set(true);

    // Trimmed on the wire: the server normalises anyway, but sending the user's stray leading
    // newlines would put them at the top of the firm's email for no reason.
    this.supportRequestApi.submit(this.descriptionControl.value.trim()).subscribe({
      next: ({ status, destination }) => {
        // Names the address the server actually used, so a client whose message went to Aiwyn's
        // support desk rather than their firm is told so even if the lookup above never answered.
        // QUEUED means the message is held on our side and has not left yet — claiming it was sent
        // would be a promise about something that has not happened.
        const confirmation =
          status === 'QUEUED'
            ? `We have your message for ${destination}. It has not gone out yet.`
            : `Your message has been sent to ${destination}.`;

        this.snackbar.open(confirmation, SnackbarType.SUCCESS);
        this.dialogRef.close(true);
      },
      error: (error: HttpErrorResponse) => {
        // Stay open and keep what they typed — retyping a long description because of a transient
        // failure is the worst outcome here. Never report success without a 2xx.
        this.isSubmitting.set(false);
        // "Try again" is wrong advice for a rate limit: the per-contact window is debited first, so
        // following it spends more of the user's own allowance and extends their own lockout.
        const message =
          error?.status === TOO_MANY_REQUESTS
            ? 'You have sent several messages recently. Please wait a few minutes and try again.'
            : 'We could not send your message. Please try again.';

        this.snackbar.open(message, SnackbarType.ERROR);
      },
    });
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
